/* eslint-disable @typescript-eslint/no-explicit-any */

import { KV } from 'yam-types'
import { Expression } from './ast'
import { ExprException } from './exception'
import * as lodash from 'lodash'

// exec enter the specified data object to execute the expression
// and return the output
// If a key is not found, it will call missingKeyCb and give the key
export default function exec(
  expr: Expression,
  data: KV,
  missingKeyCb: (key: string) => void,
  renderHelpers: { [helperName: string]: (...args: unknown[]) => unknown }
): any {
  function execInner(expr: Expression): any {
    switch (expr.type) {
      case 'Identifier':
        if (!(expr.name in data)) {
          missingKeyCb(expr.name)
          return undefined
        }
        return data[expr.name]
      case 'StringLiteral':
      case 'BoolLiteral':
      case 'NumberLiteral':
      case 'NullLiteral':
        return expr.value
      case 'ArrayLiteral':
        return expr.value.flatMap(maySpreadFactor => {
          if (maySpreadFactor.hasSpread) {
            const arr = execInner(maySpreadFactor.value)
            if (!Array.isArray(arr)) {
              throw new ExprException(maySpreadFactor.codePos, `TypeError: ${arr} is not an array`)
            }
            return execInner(maySpreadFactor.value)
          } else {
            return [execInner(maySpreadFactor.value)]
          }
        })
      case 'ObjectLiteral':
        return Object.keys(expr.value).reduce((prev, cur) => {
          prev[cur] = execInner(expr.value[cur])
          return prev
        }, {} as { [key: string]: any })
      case 'DefaultValue': {
        const res = execInner(expr.expr)
        if (res === undefined) {
          return execInner(expr.value)
        }
        return res
      }
      case 'Call': {
        const args = expr.args.map(arg => execInner(arg))
        if (expr.input !== undefined) {
          args.unshift(expr.input)
        }
        if (!(expr.func in renderHelpers)) {
          throw new ExprException(expr.codePos, `Cannot invoke an unknown function '${expr.func}'`)
        }
        return renderHelpers[expr.func].apply({}, args)
      }
      case 'Pipeline':
        return expr.chains.reduce((acc, cur) => {
          // Set the return value of the previous function
          cur.input = acc
          return execInner(cur)
        }, execInner(expr.input))
      case 'Cases': {
        const discriminant = execInner(expr.discriminant)
        for (const c of expr.caseBody.cases) {
          if (c.test.value.map(execInner).some(v => lodash.isEqual(v, discriminant))) {
            return execInner(c.value)
          }
        }
        // No case matched
        return execInner(expr.caseBody.default)
      }
      case 'BinaryExpr': {
        const left = execInner(expr.left)
        const right = execInner(expr.right)
        
        if (
          (typeof left === 'string'
            || typeof left === 'number')
          && (typeof right === 'string'
            || typeof right === 'number')
        ) {
          return execInner(expr.left) + execInner(expr.right)
        }
        throw new ExprException(expr.codePos, `Op '${expr.op}' can only be number or string on both sides, left: ${left}, right: ${right}`)
      }
    }
  }
  return execInner(expr)
}
